import { Link } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import Navbar from '../../components/feature/Navbar';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // SEO: Set document title and meta tags
  useEffect(() => {
    document.title = 'Miami Comfort Group | Strategic Real Estate Consulting in Miami';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Miami Comfort Group provides strategic real estate consulting for global investors. Expert services in property acquisition, company structuring, financing, market analysis, and property management in Miami, Florida.');
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/`);

    // Update last-modified
    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);

    // Add JSON-LD schemas
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      "name": "Miami Comfort Group",
      "description": "Strategic real estate consulting for global investors in Miami, Florida",
      "url": siteUrl,
      "logo": "https://static.readdy.ai/image/3c69b41949a07696583ba6f972aa86db/7cc47221362597664001b9fe6feac620.png",
      "image": "https://static.readdy.ai/image/3c69b41949a07696583ba6f972aa86db/7cc47221362597664001b9fe6feac620.png",
      "telephone": "+1-786-856-8145",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Miami",
        "addressRegion": "FL",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "25.7617",
        "longitude": "-80.1918"
      },
      "areaServed": {
        "@type": "City",
        "name": "Miami"
      },
      "priceRange": "$$$",
      "sameAs": [
        "https://instagram.com",
        "https://www.linkedin.com/in/elisabet-sierra/",
        "https://youtube.com"
      ]
    };

    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Real Estate Consulting",
      "provider": {
        "@type": "RealEstateAgent",
        "name": "Miami Comfort Group"
      },
      "areaServed": {
        "@type": "City",
        "name": "Miami"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Real Estate Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Strategic Real Estate Consulting"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Company & Legal Structure"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Financing & Loans"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Market Studies & Data Analysis"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Property Search & Acquisition"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Property Management (via licensed property management company)"
            }
          }
        ]
      }
    };

    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Miami Comfort Group - Strategic Real Estate Consulting",
      "description": "Strategic real estate consulting for global investors in Miami, Florida",
      "url": siteUrl,
      "mainEntity": {
        "@type": "RealEstateAgent",
        "name": "Miami Comfort Group"
      }
    };

    // Add or update organization schema
    let orgSchemaScript = document.querySelector('script[data-schema="organization"]');
    if (!orgSchemaScript) {
      orgSchemaScript = document.createElement('script');
      orgSchemaScript.setAttribute('type', 'application/ld+json');
      orgSchemaScript.setAttribute('data-schema', 'organization');
      document.head.appendChild(orgSchemaScript);
    }
    orgSchemaScript.textContent = JSON.stringify(organizationSchema);

    // Add or update service schema
    let serviceSchemaScript = document.querySelector('script[data-schema="service"]');
    if (!serviceSchemaScript) {
      serviceSchemaScript = document.createElement('script');
      serviceSchemaScript.setAttribute('type', 'application/ld+json');
      serviceSchemaScript.setAttribute('data-schema', 'service');
      document.head.appendChild(serviceSchemaScript);
    }
    serviceSchemaScript.textContent = JSON.stringify(serviceSchema);

    // Add or update webpage schema
    let webPageSchemaScript = document.querySelector('script[data-schema="webpage"]');
    if (!webPageSchemaScript) {
      webPageSchemaScript = document.createElement('script');
      webPageSchemaScript.setAttribute('type', 'application/ld+json');
      webPageSchemaScript.setAttribute('data-schema', 'webpage');
      document.head.appendChild(webPageSchemaScript);
    }
    webPageSchemaScript.textContent = JSON.stringify(webPageSchema);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    const formBody = new URLSearchParams();
    formBody.append('name', formData.name);
    formBody.append('email', formData.email);
    formBody.append('phone', formData.phone);
    formBody.append('interest', formData.interest);
    formBody.append('message', formData.message);

    try {
      const response = await fetch('https://readdy.ai/api/form/d6c8eq3v53dtlm7j8130', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          interest: '',
          message: ''
        });
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Newsletter subscription:', newsletterEmail);
  };

  // Counter animation state
  const [countersStarted, setCountersStarted] = useState(false);
  const [count15, setCount15] = useState(0);
  const [count7, setCount7] = useState(0);
  const [count100, setCount100] = useState(0);
  const whyMcgRef = useRef<HTMLElement>(null);

  const animateCounter = useCallback(
    (setter: (v: number) => void, target: number, duration: number = 1800) => {
      const startTime = performance.now();
      const step = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // ease-out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        setter(Math.round(eased * target));
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    },
    []
  );

  useEffect(() => {
    const el = whyMcgRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !countersStarted) {
          setCountersStarted(true);
          animateCounter(setCount15, 15, 1600);
          animateCounter(setCount7, 7, 1200);
          animateCounter(setCount100, 100, 2000);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [countersStarted, animateCounter]);

  return (
    <div className="min-h-screen bg-white font-['Quicksand']">
      {/* Navigation */}
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="w-full h-full object-cover object-center"
          >
            <source src="https://public.readdy.ai/ai/video_res/e2a26072-7c9d-43ed-9a91-34c587eca499.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50 md:from-black/60 md:via-black/50 md:to-black/60"></div>
        </div>
        
        <div className="relative h-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20 flex flex-col justify-center">
          <div className="max-w-full md:max-w-[650px]">
            <div className="inline-block px-4 md:px-5 py-2 rounded-full bg-[#B4E4E0]/20 backdrop-blur-sm mb-6 md:mb-10 border border-[#B4E4E0]/30">
              <span className="text-white text-[12px] md:text-[13px] font-medium tracking-wide">Strategic Real Estate Consulting</span>
            </div>
            
            <h2 className="font-['Oxygen'] font-bold text-[36px] md:text-[52px] lg:text-[58px] leading-[1.15] md:leading-[1.2] text-white mb-5 md:mb-8 tracking-tight">
              Strategic Real Estate Consulting in Miami for Global Investors
            </h2>
            
            <p className="text-white/90 text-[16px] md:text-[18px] lg:text-[19px] leading-[1.65] md:leading-[1.7] mb-8 md:mb-12 max-w-full md:max-w-[580px]">
              Strategy, structure and technology for intelligent real estate decisions in the U.S.
            </p>
            
            <a 
              href="#contact" 
              className="inline-flex items-center justify-center w-full sm:w-auto px-7 sm:px-8 py-4 bg-[#A7D0CC] rounded-lg hover:bg-[#8CBEB9] transition-all duration-300 cursor-pointer shadow-md hover:shadow-[0_4px_12px_rgba(167,208,204,0.3)] min-h-[48px] whitespace-nowrap"
            >
              <span className="text-black text-[15px] sm:text-[16px] font-bold font-['Oxygen']">Schedule a Private Consultation</span>
            </a>
          </div>
          
          <div className="hidden md:block absolute bottom-[100px] right-10 lg:right-20 text-white text-[14px] transform rotate-90 origin-right">
            Miami, FL
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="relative bg-white py-16 md:py-24 lg:py-32">
        <div className="absolute left-0 top-0 bottom-0 w-1 md:w-2 bg-[#E8E2DB]"></div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 md:gap-16 lg:gap-20">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-10 md:mb-16">
                <div className="w-2.5 md:w-3 h-2.5 md:h-3 rounded-full bg-[#B4E4E0]"></div>
                <span className="text-black text-[12px] md:text-[14px] uppercase tracking-[2px] font-medium">About MCG</span>
              </div>
              
              <h2 className="font-['Oxygen'] text-[36px] md:text-[48px] lg:text-[56px] leading-[1.15] text-black mb-8 md:mb-12">
                Where Experience Meets<br />
                <span className="font-bold">Strategic Innovation</span>
              </h2>
              
              <div className="space-y-4 md:space-y-6">
                <p className="text-[#333333] text-[16px] md:text-[18px]">Human expertise + Technology</p>
                <p className="text-[#333333] text-[16px] md:text-[18px]">Strategy before execution</p>
                <p className="text-[#333333] text-[16px] md:text-[18px]">Long-term vision</p>
              </div>
            </div>
            
            <div className="lg:col-span-3">
              <div className="max-w-full lg:max-w-[580px] space-y-6 md:space-y-8">
                <p className="text-[#333333] text-[16px] md:text-[18px] leading-[1.8]">
                  Miami Comfort Group is not a traditional real estate agency—we are a strategic partner for foreign and domestic investors seeking to enter or expand in the U.S. real estate market with clarity, structure, and long-term vision.
                </p>
                
                <div className="border-l-4 border-[#B4E4E0] pl-6 md:pl-8 py-4">
                  <p className="font-['Oxygen'] font-bold text-[20px] md:text-[24px] text-black leading-[1.4]">
                    "Invest well starts with deciding well."
                  </p>
                </div>
                
                <p className="text-[#333333] text-[16px] md:text-[18px] leading-[1.8]">
                  Based in Miami, Florida, we combine decades of real estate experience with cutting-edge technology to provide comprehensive consulting services. From company structuring and financing strategies to market analysis and property management, we guide our clients through every step of their investment journey.
                </p>
                
                <div className="bg-[#E8E2DB] rounded-2xl p-6 md:p-8">
                  <p className="text-[#333333] text-[15px] md:text-[16px] italic leading-[1.7]">
                    Our mission is to provide peace of mind and security for investors who value strategy over speed, clarity over noise, and vision over trends. We are your single point of contact for intelligent real estate decisions in the United States.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="bg-[#E8E2DB] py-16 md:py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20">
          <div className="text-center mb-12 md:mb-16 lg:mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#B4E4E0]/30 mb-4 md:mb-6">
              <span className="text-black text-[12px] md:text-[14px] uppercase tracking-[2px] font-medium">Core Services</span>
            </div>
            <h2 className="font-['Oxygen'] text-[36px] md:text-[52px] lg:text-[64px] leading-[1.2] text-black mb-6 md:mb-8">
              Comprehensive Real Estate<br />
              Consulting Solutions
            </h2>
            <p className="text-[#666666] text-[16px] md:text-[18px] lg:text-[20px]">Six strategic pillars for intelligent investment decisions</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-10">
            {[
              {
                number: '01',
                icon: 'ri-line-chart-line',
                title: 'Strategic Real Estate Consulting',
                description: 'Define financial goals, create personalized investment strategies, and develop comprehensive real estate roadmaps.',
                features: ['Financial goal definition', 'Risk analysis & mitigation', 'Short/medium/long-term planning']
              },
              {
                number: '02',
                icon: 'ri-building-line',
                title: 'Company & Legal Structure',
                description: 'LLC/Corp creation, coordination with legal and tax specialists, and asset structuring optimization.',
                features: ['Entity formation & registration', 'Tax optimization strategies', 'Asset protection planning'],
                disclaimer: 'Legal and tax services are provided by independent, Florida-licensed attorneys and CPAs. Miami Comfort Group does not provide legal or tax advice.'
              },
              {
                number: '03',
                icon: 'ri-bank-line',
                title: 'Financing & Loans',
                description: 'Eligibility analysis, financing strategies with or without U.S. credit, and lender coordination.',
                features: ['Credit analysis & strategy', 'Lender network access', 'Cash flow optimization'],
                disclaimer: 'Financing services are coordinated with independent, NMLS-licensed mortgage professionals. Miami Comfort Group does not originate loans or provide mortgage advice.'
              },
              {
                number: '04',
                icon: 'ri-bar-chart-box-line',
                title: 'Market Studies & Data Analysis',
                description: 'Comprehensive market studies, comparative analysis, and predictive profitability scenarios.',
                features: ['Zone-specific market research', 'Historical profitability analysis', 'Predictive modeling']
              },
              {
                number: '05',
                icon: 'ri-home-smile-line',
                title: 'Property Search & Acquisition',
                description: 'Residential and investment property search, new construction opportunities, negotiation and closing.',
                features: ['Curated property selection', 'Expert negotiation', 'Seamless closing process']
              },
              {
                number: '06',
                icon: 'ri-settings-3-line',
                title: 'Property Management',
                description: 'Provided by a licensed property management company within the MCG group — independent from real estate brokerage services. Inquire for details.',
                features: ['Full-service management', 'Rental income optimization', 'Real-time reporting dashboards'],
                disclaimer: 'Provided by an independent property management company within the MCG group. We provide you with a curated selection of vetted professionals so you can choose the best fit for your needs. This service is not provided by Avanti Way Realty, LLC.'
              }
            ].map((service, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 md:p-10 lg:p-12 shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.12)] hover:-translate-y-2 transition-all duration-300 cursor-pointer min-h-[380px] md:min-h-[420px] flex flex-col group"
              >
                <div className="flex items-start justify-between mb-6 md:mb-8">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center">
                    <i className={`${service.icon} text-[40px] md:text-[48px] text-black group-hover:text-[#B4E4E0] transition-colors`}></i>
                  </div>
                  <span className="font-['Oxygen'] font-bold text-[16px] md:text-[18px] text-[#D4AF8C]">{service.number}</span>
                </div>
                
                <h3 className="font-['Oxygen'] font-bold text-[24px] md:text-[28px] leading-[1.2] text-black mb-4 md:mb-6">
                  {service.title}
                </h3>
                
                <p className="text-[#333333] text-[15px] md:text-[16px] leading-[1.7] mb-4 md:mb-6">
                  {service.description}
                </p>
                
                <div className="space-y-2 md:space-y-3 mt-auto">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#B4E4E0] mt-2 flex-shrink-0"></div>
                      <span className="text-[#666666] text-[14px] md:text-[15px] leading-[1.6]">{feature}</span>
                    </div>
                  ))}
                </div>

                {'disclaimer' in service && service.disclaimer && (
                  <p className="mt-4 pt-3 border-t border-[#E8E2DB] text-[12px] md:text-[13px] text-[#999999] italic leading-[1.5]">
                    {service.disclaimer}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Founder */}
      <section className="bg-white py-16 md:py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 lg:gap-20 items-center">
            <div className="flex flex-col items-center text-center">
              <div className="w-52 h-52 md:w-64 md:h-64 rounded-full overflow-hidden border-4 border-[#E8E2DB] shadow-[0_8px_32px_rgba(0,0,0,0.1)]">
                <img 
                  src="https://static.readdy.ai/image/3c69b41949a07696583ba6f972aa86db/9029bce373ac6db607735eaa6d451a75.jpeg" 
                  alt="Elisabet Sierra - Licensed Real Estate Sales Associate at Miami Comfort Group"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
            
            <div>
              <div className="flex items-center gap-3 mb-6 md:mb-8">
                <div className="w-2.5 h-2.5 rounded-full bg-[#B4E4E0]"></div>
                <span className="text-black text-[12px] md:text-[14px] font-medium px-3 md:px-4 py-1.5 rounded-full bg-[#B4E4E0]/20 backdrop-blur-sm border border-[#B4E4E0]/30">Meet the Founder</span>
              </div>
              
              <h2 className="font-['Oxygen'] font-bold text-[32px] md:text-[40px] lg:text-[48px] leading-[1.15] text-black mb-2">
                Elisabet Sierra
              </h2>
              
              <p className="text-[#333333] text-[15px] md:text-[17px] mb-1">
                Licensed Real Estate Sales Associate | Avanti Way Realty, LLC
              </p>
              <p className="text-[#888888] text-[13px] md:text-[14px] mb-6 md:mb-8">
                License #SL3535387 - Elisabet Sierra Bermejo PA
              </p>
              
              <p className="text-[#333333] text-[16px] md:text-[18px] leading-[1.8] mb-8 md:mb-10">
                Born in Spain, relocated across three continents before making Miami home. I built MCG because I lived the relocation experience myself — and I know what executives and families need to land well in a new city.
              </p>
              
              <Link 
                to="/relocation" 
                className="inline-flex items-center justify-center px-8 py-4 bg-[#EC9982] rounded-lg hover:bg-[#E08A72] transition-all duration-300 cursor-pointer shadow-md hover:shadow-[0_4px_12px_rgba(236,153,130,0.35)] whitespace-nowrap"
              >
                <span className="text-white text-[15px] md:text-[16px] font-bold font-['Oxygen']">Book a Free Consultation</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Showcase */}
      <section id="technology" className="relative py-16 md:py-24 lg:py-32 overflow-hidden" style={{
        backgroundImage: 'url(https://static.readdy.ai/image/3c69b41949a07696583ba6f972aa86db/b296654349c2f6013f4e47c2003904ae.png)',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat'
      }}>
        {/* Reduced Overlay for Visibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/35 to-black/30"></div>
        
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20">
          <div className="max-w-[800px]">
            <div className="flex items-center gap-3 mb-8 md:mb-10">
              <div className="w-2 h-2 rounded-full bg-[#B4E4E0]"></div>
              <span className="text-white text-[12px] md:text-[14px] font-medium px-3 md:px-4 py-1.5 rounded-full bg-[#B4E4E0]/20 backdrop-blur-sm border border-[#B4E4E0]/30">Innovation & Technology</span>
            </div>
            
            <h2 className="font-['Oxygen'] text-[36px] md:text-[48px] lg:text-[56px] leading-[1.15] text-white mb-6 md:mb-8">
              <span className="font-bold">Simplifying Real Estate Operations</span><br />
              <span className="font-normal text-white/90">Through Intelligent Automation</span>
            </h2>
            
            <p className="text-white/80 text-[16px] md:text-[18px] lg:text-[19px] leading-[1.7] mb-10 md:mb-14 max-w-full lg:max-w-[680px]">
              Our technology platform works behind the scenes to streamline property management, automate workflows, and provide real-time operational intelligence—giving you complete control without the complexity.
            </p>
            
            <div className="space-y-5 md:space-y-6">
              {[
                {
                  title: 'Automated Workflows',
                  description: 'Streamline tenant communications, maintenance requests, and lease renewals'
                },
                {
                  title: 'Real-Time Operations Dashboard',
                  description: 'Monitor property performance, occupancy, and financial metrics at a glance'
                },
                {
                  title: 'Intelligent Communication Hub',
                  description: 'Centralized messaging system for tenants, vendors, and property teams'
                },
                {
                  title: 'Financial Reporting & Analytics',
                  description: 'Automated income tracking, expense management, and investment performance'
                },
                {
                  title: 'Seamless Integrations',
                  description: 'Connect with accounting, legal, and financial systems for unified operations'
                }
              ].map((feature, index) => (
                <div key={index} className="flex items-start gap-4 group">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-[20px] text-[#B4E4E0] group-hover:text-[#D4AF8C] transition-colors"></i>
                  </div>
                  <div>
                    <h3 className="text-white text-[16px] md:text-[17px] font-medium mb-1">{feature.title}</h3>
                    <p className="text-white/60 text-[14px] md:text-[15px] leading-[1.6]">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose MCG - Stats */}
      <section id="why-mcg" ref={whyMcgRef} className="bg-black py-20 md:py-28 lg:py-36">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16 lg:gap-20 text-center mb-12 md:mb-16">
            <div>
              <span className="font-['Oxygen'] font-bold text-[56px] md:text-[72px] lg:text-[88px] text-white leading-none block">
                {count15}+
              </span>
              <span className="text-white/60 text-[15px] md:text-[17px] mt-3 block">Countries represented</span>
            </div>
            <div>
              <span className="font-['Oxygen'] font-bold text-[56px] md:text-[72px] lg:text-[88px] text-white leading-none block">
                {count7}
              </span>
              <span className="text-white/60 text-[15px] md:text-[17px] mt-3 block">Service areas under one roof</span>
            </div>
            <div>
              <span className="font-['Oxygen'] font-bold text-[56px] md:text-[72px] lg:text-[88px] text-white leading-none block">
                {count100}%
              </span>
              <span className="text-white/60 text-[15px] md:text-[17px] mt-3 block">Single point of contact</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative bg-black py-16 md:py-24 lg:py-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(180,228,224,0.05)_0%,transparent_70%)]"></div>
        
        <div className="relative max-w-[680px] mx-auto px-6 md:px-10 lg:px-20">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-['Oxygen'] font-bold text-[40px] md:text-[52px] lg:text-[56px] text-white mb-4 md:mb-6">
              Let's Connect
            </h2>
            <p className="text-white/85 text-[16px] md:text-[18px] lg:text-[20px] max-w-[540px] mx-auto">
              Schedule a private consultation to discuss your real estate investment strategy
            </p>
          </div>
          
          {submitSuccess ? (
            <div className="bg-[#B4E4E0]/20 border border-[#B4E4E0]/40 rounded-2xl p-6 sm:p-8 md:p-12 text-center">
              <div className="w-14 sm:w-16 h-14 sm:h-16 mx-auto mb-4 sm:mb-6 rounded-full bg-[#B4E4E0] flex items-center justify-center">
                <i className="ri-check-line text-[28px] sm:text-[32px] text-black"></i>
              </div>
              <h3 className="font-['Oxygen'] font-bold text-[22px] sm:text-[24px] md:text-[28px] text-white mb-3 sm:mb-4">
                Message Received!
              </h3>
              <p className="text-white/80 text-[14px] sm:text-[16px] md:text-[18px]">
                Thank you for reaching out. We'll contact you within 24 hours.
              </p>
            </div>
          ) : (
            <form 
              id="contact-consultation-form"
              data-readdy-form
              onSubmit={handleSubmit} 
              className="space-y-6 md:space-y-8"
            >
              <input
                type="text"
                name="name"
                placeholder="Full Name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full h-14 md:h-16 bg-transparent border border-white/30 rounded-xl px-5 md:px-6 text-white placeholder-white/50 focus:border-[#B4E4E0] focus:outline-none transition-colors text-[15px] md:text-[16px]"
                required
              />
              
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full h-14 md:h-16 bg-transparent border border-white/30 rounded-xl px-5 md:px-6 text-white placeholder-white/50 focus:border-[#B4E4E0] focus:outline-none transition-colors text-[15px] md:text-[16px]"
                required
              />
              
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full h-14 md:h-16 bg-transparent border border-white/30 rounded-xl px-5 md:px-6 text-white placeholder-white/50 focus:border-[#B4E4E0] focus:outline-none transition-colors text-[15px] md:text-[16px]"
                required
              />
              
              <select
                name="interest"
                value={formData.interest}
                onChange={(e) => setFormData({...formData, interest: e.target.value})}
                className="w-full h-14 md:h-16 bg-transparent border border-white/30 rounded-xl px-5 md:px-6 text-white focus:border-[#B4E4E0] focus:outline-none transition-colors text-[15px] md:text-[16px] cursor-pointer"
                required
              >
                <option value="" className="bg-black">Investment Interest</option>
                <option value="strategic-consult-ing" className="bg-black">Strategic Real Estate Consulting</option>
                <option value="company-structure" className="bg-black">Company & Legal Structure</option>
                <option value="financing" className="bg-black">Financing & Loans</option>
                <option value="market-studies" className="bg-black">Market Studies & Data Analysis</option>
                <option value="property-search" className="bg-black">Property Search & Acquisition</option>
                <option value="property-management" className="bg-black">Property Management</option>
              </select>
              
              <textarea
                name="message"
                placeholder="Tell us about your investment goals..."
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value.slice(0, 500)})}
                className="w-full h-32 md:h-40 bg-transparent border border-white/30 rounded-xl px-5 md:px-6 py-4 text-white placeholder-white/50 focus:border-[#B4E4E0] focus:outline-none transition-colors text-[15px] md:text-[16px] resize-none"
                maxLength={500}
                required
              ></textarea>
              
              {submitError && (
                <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3 sm:p-4 text-red-300 text-[13px] sm:text-[14px]">
                  {submitError}
                </div>
              )}
              
              <button 
                type="submit"
                disabled={isSubmitting}
                className="w-full h-[56px] sm:h-[60px] bg-[#A7D0CC] rounded-lg hover:bg-[#8CBEB9] transition-all duration-300 flex items-center justify-center cursor-pointer shadow-md hover:shadow-[0_4px_12px_rgba(167,208,204,0.3)] min-h-[48px] whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="font-['Oxygen'] font-bold text-[16px] sm:text-[18px] text-black">
                  {isSubmitting ? 'Sending...' : 'Schedule Consultation'}
                </span>
              </button>
            </form>
          )}
          
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10 text-white text-[14px] md:text-[16px] mt-10">
            <a href="mailto:hello@miamicomfort.group" className="flex items-center gap-3 hover:text-[#B4E4E0] transition-colors cursor-pointer">
              <i className="ri-user-line text-[#B4E4E0]"></i>
              <span className="whitespace-nowrap">Elisabet Sierra Bermejo</span>
            </a>
            <div className="hidden md:block w-px h-5 bg-white/20"></div>
            <a href="tel:+17868568145" className="flex items-center gap-3 hover:text-[#B4E4E0] transition-colors cursor-pointer">
              <i className="ri-phone-line text-[#B4E4E0]"></i>
              <span className="whitespace-nowrap">+1 786 856 8145</span>
            </a>
            <div className="hidden md:block w-px h-5 bg-white/20"></div>
            <div className="flex items-center gap-3">
              <i className="ri-map-pin-line text-[#B4E4E0]"></i>
              <span className="whitespace-nowrap">Miami, FL, USA</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black relative overflow-hidden">
        <div className="absolute bottom-0 left-0 right-0 font-['Oxygen'] font-bold text-[100px] md:text-[140px] lg:text-[180px] text-white/[0.03] text-center leading-none pointer-events-none select-none" style={{transform: 'translateY(40px)'}}>
          MCG
        </div>
        
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20 py-12 md:py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12 lg:gap-16">
            <div>
              <img 
                src="https://static.readdy.ai/image/3c69b41949a07696583ba6f972aa86db/7cc47221362597664001b9fe6feac620.png" 
                alt="Miami Comfort Group" 
                className="h-10 md:h-12 w-auto object-contain brightness-0 invert"
              />
              <h3 className="font-['Oxygen'] font-bold text-[24px] md:text-[28px] lg:text-[32px] text-white leading-[1.2] mb-6 md:mb-8">
                Strategic Real Estate<br />Consulting
              </h3>
              <p className="text-white/70 text-[13px] md:text-[14px] leading-[1.6]">
                Investing well starts with deciding well
              </p>
            </div>
            
            <div>
              <h4 className="font-['Oxygen'] font-bold text-[14px] md:text-[16px] text-white uppercase tracking-wider mb-4 md:mb-6">Services</h4>
              <div className="space-y-3 md:space-y-4">
                {[
                  'Strategic Consulting',
                  'Company & Legal Structure',
                  'Financing & Loans',
                  'Market Studies',
                  'Property Search',
                  'Property Management'
                ].map((link, index) => (
                  <div key={index}>
                    <a href="#services" className="text-white/80 text-[14px] md:text-[15px] hover:text-[#B4E4E0] hover:opacity-100 transition-colors cursor-pointer block">
                      {link}
                    </a>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-['Oxygen'] font-bold text-[14px] md:text-[16px] text-white uppercase tracking-wider mb-4 md:mb-6">Company</h4>
              <div className="space-y-3 md:space-y-4">
                <Link to="/about" className="text-white/80 text-[14px] md:text-[15px] hover:text-[#B4E4E0] hover:opacity-100 transition-colors block">
                  About Us
                </Link>
                <a href="#technology" className="text-white/80 text-[14px] md:text-[15px] hover:text-[#B4E4E0] hover:opacity-100 transition-colors cursor-pointer block">
                  Technology
                </a>
                <a href="#why-mcg" className="text-white/80 text-[14px] md:text-[15px] hover:text-[#B4E4E0] hover:opacity-100 transition-colors cursor-pointer block">
                  Why MCG
                </a>
                <a href="#contact" className="text-white/80 text-[14px] md:text-[15px] hover:text-[#B4E4E0] hover:opacity-100 transition-colors cursor-pointer block">
                  Contact
                </a>
                
                <div className="flex items-center gap-4 md:gap-5 mt-8 md:mt-10">
                  <a href="https://www.instagram.com/elisabet_comfyhouses/" target="_blank" rel="noopener noreferrer" className="w-6 h-6 flex items-center justify-center cursor-pointer">
                    <i className="ri-instagram-line text-[22px] md:text-[24px] text-white/60 hover:text-[#B4E4E0] transition-colors"></i>
                  </a>
                  <a href="https://www.linkedin.com/in/elisabet-sierra/" target="_blank" rel="noopener noreferrer" className="w-6 h-6 flex items-center justify-center cursor-pointer">
                    <i className="ri-linkedin-line text-[22px] md:text-[24px] text-white/60 hover:text-[#B4E4E0] transition-colors"></i>
                  </a>
                  <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="w-6 h-6 flex items-center justify-center cursor-pointer">
                    <i className="ri-youtube-line text-[22px] md:text-[24px] text-white/60 hover:text-[#B4E4E0] transition-colors"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-12 md:mt-16 lg:mt-20 pt-6 md:pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-[13px] md:text-[14px] text-center md:text-left">
              © 2026 Miami Comfort Group. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <Link 
                to="/privacy-policy"
                className="text-white/50 text-[13px] md:text-[14px] hover:text-[#B4E4E0] transition-colors cursor-pointer"
              >
                Privacy Policy
              </Link>
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/50 text-[13px] md:text-[14px] hover:text-[#B4E4E0] transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>

          {/* Legal Footer Text */}
          <div className="mt-8 pt-6 border-t border-white/10 text-center space-y-3">
            <p className="text-[11px] font-semibold text-[#cccccc]">
              Elisabet Sierra Bermejo · Licensed Real Estate Sales Associate<br />
              Avanti Way Realty, LLC · License #SL3535387
            </p>
            <p className="text-[10px] text-[#888888] max-w-[800px] mx-auto leading-relaxed">
              Miami Comfort Group LLC is a brand name used for marketing purposes only. 
              Real estate services are provided by Elisabet Sierra Bermejo, licensed 
              with Avanti Way Realty, LLC.
            </p>
            <p className="text-[10px] text-[#888888]">
              © 2026 Miami Comfort Group · miamicomfort.group · hello@miamicomfort.group
            </p>
            <p className="text-[9px] text-[#888888] max-w-[700px] mx-auto leading-relaxed pt-2">
              Equal Housing Opportunity. We are pledged to the letter and spirit of U.S. policy for equal housing opportunity throughout the nation.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}